/**
 * HollyMessage — individual chat message bubble.
 *
 * Layout:
 *   Holly: FloxOLogo avatar on the LEFT, bubble + timestamp stacked beside it.
 *          Sharp bottom-left corner. Background #F3F4F6.
 *   User:  Right-aligned. Bubble + timestamp stacked. Sharp bottom-right corner.
 *          Background brand blue (#3574E3), white text.
 *
 * When a Holly response carries warehouse results, the cards render at FULL
 * panel width ABOVE the text bubble (matches the SaaS pattern), so the text
 * always settles directly above the next conversation turn.
 */

import type { HollyMessage as HollyMessageType } from './types';
import FloxOLogo from './FloxOLogo';
import HollyWarehouseCard from './HollyWarehouseCard';
import HollyTransportationCard from './HollyTransportationCard';
import { formatHollyMarkdown } from './format';

interface HollyMessageProps {
  message: HollyMessageType;
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const hh = hours < 10 ? `0${hours}` : `${hours}`;
  return `${hh}:${mm} ${period}`;
}

/** Render a single chat-style bubble with the brand avatar (Holly only) and
 *  optional timestamp. Factored out so the same JSX serves both the pre-card
 *  lead-in bubble and the main post-card bubble. */
function Bubble({
  isHolly,
  html,
  timestamp,
  marginBottom = 0,
}: {
  isHolly: boolean;
  html: string;
  timestamp?: string;
  marginBottom?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: isHolly ? 'flex-start' : 'flex-end',
        gap: 8,
        width: '100%',
        marginBottom,
      }}
    >
      {isHolly && <FloxOLogo size={28} variant='colour' className='shrink-0' />}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isHolly ? 'flex-start' : 'flex-end',
          maxWidth: '78%',
          minWidth: 0,
        }}
      >
        <div
          style={{
            padding: '10px 14px',
            borderRadius: isHolly ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
            backgroundColor: isHolly ? '#F3F4F6' : '#3574E3',
            color: isHolly ? '#1E1E2F' : '#FFFFFF',
            fontSize: 14,
            lineHeight: '20px',
            fontFamily: "'Poppins', sans-serif",
            wordBreak: 'break-word' as const,
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {timestamp && (
          <span
            style={{
              marginTop: 4,
              fontSize: 11,
              color: '#9CA3AF',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {timestamp}
          </span>
        )}
      </div>
    </div>
  );
}

export default function HollyMessageBubble({ message }: HollyMessageProps) {
  const isHolly = message.role === 'holly';
  const hasWarehouses =
    isHolly && message.warehouses && message.warehouses.length > 0;
  const hasQuotes =
    isHolly &&
    message.transportationQuotes &&
    message.transportationQuotes.length > 0;
  const hasContent = message.content && message.content.trim().length > 0;
  const hasPreCard =
    isHolly &&
    typeof message.preCardContent === 'string' &&
    message.preCardContent.trim().length > 0;
  const timestamp = formatTimestamp(message.timestamp);

  return (
    <div
      className='holly-animate'
      style={{
        marginBottom: 16,
        animation: 'holly-fade-in var(--duration-fast) var(--ease-out)',
      }}
    >
      {/* Pre-card lead-in bubble — only rendered when the backend provided a
          dedicated lead-in (currently the transport-quote ESTIMATE_SHOWN turn,
          per the design's "lead-in above card" layout). No timestamp on this
          bubble; the post-card bubble below carries the single per-turn
          timestamp so we don't render the same time twice for one response. */}
      {hasPreCard && (
        <Bubble
          isHolly={isHolly}
          html={formatHollyMarkdown(message.preCardContent!)}
          marginBottom={12}
        />
      )}

      {/* Transportation quote cards — full width, rendered ABOVE warehouses + text. */}
      {hasQuotes && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: '100%',
            marginBottom: hasWarehouses || hasContent ? 12 : 0,
          }}
        >
          {message.transportationQuotes!.map((quote) => (
            <HollyTransportationCard key={quote.id} quote={quote} />
          ))}
        </div>
      )}

      {/* Warehouse cards — full width, rendered ABOVE the text response. */}
      {hasWarehouses && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: '100%',
            marginBottom: hasContent ? 12 : 0,
          }}
        >
          {message.warehouses!.map((wh, i) => (
            <HollyWarehouseCard
              key={wh.id}
              warehouse={wh}
              warehouseIndex={i + 1}
            />
          ))}
        </div>
      )}

      {/* Main text bubble + timestamp. */}
      {hasContent && (
        <Bubble
          isHolly={isHolly}
          html={formatHollyMarkdown(message.content)}
          timestamp={timestamp}
        />
      )}
    </div>
  );
}
